// THE CUSTOM CATEGORIES THAT EXIST, AND A WAY TO SELECT ONE.
//
// Owner, 2026-10-03: "then they get filtered and selected like any other
// categories elements".
//
// Creating one has worked since 2026-09-24. Reading the list back had NO
// consumer at all - GET /derived-categories was written, tested and called
// by nothing - so after creating "Beam clamps" there was nowhere to see it,
// and nothing to click. This is that list.
//
// IT SELECTS, IT DOES NOT TRACK. Selecting hands the page the part ids and
// everything the page already does with a selection follows: the count in
// the bulk bar, the colour in the model, Isolate, Set status. What it does
// NOT do is tick the saved scope - a derived category inside the scope
// would add the parts to totals that already count their parents, which is
// the double count models/derivedCategory exists to refuse. When per-part
// tracking lands (the owner's 2026-10-03 ruling, "yes real tracked elements
// with their quantities") that is a decision about the SCOPE, made there.
//
// A REFUSAL IS THE COUNT, NOT A SHRUG. Over the resolver's limit the server
// refuses the whole list rather than returning part of it, and this says how
// many there were, because "pick a narrower part type - there are 255,396"
// is something a person can act on.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Popconfirm, Spin, Tooltip, message } from "antd";
import { stageContainer } from "./stagePopup";
import selectDerivedParts from "./selectDerivedParts";
import { deleteDerivedCategory, getDerivedCategories } from "api/tracking/trackingElementsApi";

// Local, as every sibling panel does it - trackingTheme exports the STATUS
// tables, not this. Imported from there it would have been undefined and
// the first style read would have thrown on render.
const T = { muted: "#6B7280", ink: "#111827", line: "#E5E7EB" };

const n = (v) => Number(v || 0).toLocaleString();

// WHAT A ROW IS ACTUALLY TRACKING, in the words the person ticked.
//
// The owner, 2026-10-04: "there is no indication what subpart they are
// referencing of what category/element". A name like "Beam clamps" says
// nothing about which part families it holds, and a definition may now hold
// SEVERAL (models/derivedCategory `parts`), so this spells them out and only
// falls back to the single `part` for definitions made before that existed.
//
// A tick with no family and no type means EVERY part of the source, and it
// says so - that is a materially different category from a named one, and
// the difference is the whole question he was asking.
function referencesOf(row) {
  const src = (row && row.source) || {};
  const from = [src.category, src.family, src.type].filter(Boolean).join(" / ") || "any category";
  const list = Array.isArray(row && row.parts) && row.parts.length
    ? row.parts
    : [(row && row.part) || {}];
  const named = list
    .map((p) => [p.family, p.type].filter(Boolean).join(" · "))
    .filter(Boolean);
  const of = named.length ? named.join("  +  ") : "every part";
  return { of, from, count: named.length };
}

export default function CustomCategoryList({ projectId, documentIds = [], onSelect = null, reloadKey = 0, canEdit = false, onChanged = null }) {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [deletingId, setDeletingId] = useState("");

  // REMOVING ONE IS A SOFT DELETE server-side, so the statuses recorded on
  // its parts are untouched - only the definition goes. Said in the confirm,
  // because "remove" on a thing that has progress against it should not
  // leave a person wondering what happened to the work.
  const remove = async (row) => {
    const id = String(row._id || row.id || "");
    if (!id) return;
    setDeletingId(id);
    try {
      await deleteDerivedCategory(projectId, id);
      setRows((prev) => (prev || []).filter((r) => String(r._id || r.id) !== id));
      message.success(`Removed ${row.name}`);
      if (onChanged) onChanged();
    } catch (e) {
      message.error(e.message || "Could not remove that category");
    } finally {
      setDeletingId("");
    }
  };

  // A STRING, NOT THE ARRAY. `documentIds` is a fresh array on every render
  // of the page above (and defaults to a new [] here), so depending on the
  // array itself re-ran this effect for ever: load, setRows, render, new
  // array, load. It hung jest for 25 minutes before it could have hung a
  // browser, and the fix is the same either way - depend on the VALUE.
  const docKey = (documentIds || []).join(",");

  useEffect(() => {
    let alive = true;
    setRows(null);
    // WITH THE NUMBERS. The route only computes them when asked (totals=1),
    // and without that every row printed its name over an empty line - which
    // is what the owner saw as "no indication what subpart they are
    // referencing" (2026-10-04). Half of that was mine: I never asked.
    getDerivedCategories(projectId, { totals: 1, documentIds: docKey || undefined })
      .then((r) => { if (alive) setRows((r && r.categories) || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [projectId, docKey, reloadKey]);

  const select = useCallback(async (row) => {
    const id = String(row._id || row.id || "");
    if (!id || !onSelect) return;
    setBusyId(id);
    try {
      // One path, shared with the row in the model tree.
      await selectDerivedParts({ projectId, documentIds: docKey, id, name: row.name, onSelect });
    } catch (e) {
      message.error(e.message || "Could not read that custom category");
    } finally {
      setBusyId("");
    }
  }, [projectId, docKey, onSelect]);

  if (rows === null) return <div style={{ padding: 12 }}><Spin size="small" /></div>;
  if (!rows.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<span style={{ fontSize: 12.5 }}>No custom categories yet. Pick a category above, then its parts.</span>}
      />
    );
  }
  return (
    <div>
      {rows.map((row) => {
        const id = String(row._id || row.id || "");
        const t = row.totals || {};
        const ref = referencesOf(row);
        return (
          <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: `1px solid ${T.line}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 12.5 }}>{row.name}</div>
              <div style={{ fontSize: 11.5, color: T.muted }}>
                <span style={{ color: T.ink }}>{ref.of}</span>
                {" in "}
                {ref.from}
              </div>
            </div>
            <div style={{ fontSize: 12, whiteSpace: "nowrap", color: T.muted }}>
              {t.parts !== undefined ? `${n(t.parts)} parts` : ""}
              {t.parents !== undefined ? ` · in ${n(t.parents)} elements` : ""}
            </div>
            {onSelect ? (
              <Tooltip title="Select these parts in the model - then Isolate or Set status as usual">
                <Button size="small" loading={busyId === id} onClick={() => select(row)}>Select in model</Button>
              </Tooltip>
            ) : null}
            {canEdit ? (
              <Popconfirm
                getPopupContainer={stageContainer}
                title={`Remove "${row.name}"?`}
                description="The statuses already recorded on its parts stay; only the category goes."
                okText="Remove" cancelText="Keep"
                onConfirm={() => remove(row)}
              >
                <Button size="small" danger loading={deletingId === id}>Remove</Button>
              </Popconfirm>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
